import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload, ArrowLeft, Trash2, CheckCircle2, AlertCircle, Image as ImageIcon, X, Sparkles, Wand2, Layers,
  HelpCircle, ChevronDown, ChevronUp, Plus, GripVertical, Star,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';

/**
 * ProductDraftsManager — Artikel-Vorbereitungs-Modus
 *
 * Drag-and-Drop / File-Picker für Massen-Bild-Upload. Bilder werden im
 * Backend automatisch nach Dateinamen-Pattern in Gruppen sortiert. Pro
 * Gruppe entsteht ein Draft-Produkt, das nur du im Admin siehst – nicht
 * öffentlich. Mit der Schnelleingabe-Tabelle vervollständigst du Name,
 * Preis, Kategorie usw. und veröffentlichst dann einzeln oder per
 * Bulk-Aktion.
 */
const ProductDraftsManager = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [categories, setCategories] = useState([]);
  const [aiBusyId, setAiBusyId] = useState(null);          // single-draft analysis
  const [aiSuggestionFor, setAiSuggestionFor] = useState(null); // {draft, suggestion}
  const [clusterBusy, setClusterBusy] = useState(false);
  const [clusterGroups, setClusterGroups] = useState(null);     // open cluster-dialog when array
  // Duplicate detector
  const [duplicateInfo, setDuplicateInfo] = useState(null); // {draftIds: [...], duplicatesByDraft: {draftId: [...]}, mode: 'single'|'bulk'}
  // Collapsible help box (closed by default)
  const [helpOpen, setHelpOpen] = useState(false);
  // Per-draft inline file inputs for "append images"
  const [appendBusyId, setAppendBusyId] = useState(null);
  // Bulk image selection — set of "draftId:imageIdx" strings
  const [selectedImages, setSelectedImages] = useState(new Set());
  const [bulkMoveTarget, setBulkMoveTarget] = useState('');
  const [toolbarMinimized, setToolbarMinimized] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [subcategoryConfig, setSubcategoryConfig] = useState({});
  // Auto-cluster: pending suggestion banner state
  const [pendingClusterGroups, setPendingClusterGroups] = useState([]); // groups stashed for the banner
  const [autoClusterRunning, setAutoClusterRunning] = useState(false);

  const toggleImageSelection = (draftId, idx) => {
    const key = `${draftId}:${idx}`;
    setSelectedImages((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const clearImageSelection = () => setSelectedImages(new Set());

  // Group selected images by their source draftId. Returns {draftId: [idx, idx, …]}
  const groupSelectionBySource = (selection) => {
    const byDraft = {};
    selection.forEach((key) => {
      const [draftId, idxStr] = key.split(':');
      const idx = parseInt(idxStr, 10);
      if (!byDraft[draftId]) byDraft[draftId] = [];
      byDraft[draftId].push(idx);
    });
    return byDraft;
  };

  // Bulk MOVE all selected images into `targetDraftId`. Skips selections already on the target.
  const bulkMoveImages = async (targetDraftId) => {
    if (!targetDraftId) {
      toast.error('Bitte Ziel-Artikel auswählen');
      return;
    }
    const target = drafts.find((d) => d.id === targetDraftId);
    if (!target) {
      toast.error('Ziel-Artikel nicht gefunden');
      return;
    }
    const byDraft = groupSelectionBySource(selectedImages);
    // Collect URLs to move; drop selections already on target
    const collected = [];
    const newImagesByDraft = {};
    Object.entries(byDraft).forEach(([draftId, idxList]) => {
      if (draftId === targetDraftId) return; // skip — already there
      const d = drafts.find((x) => x.id === draftId);
      if (!d) return;
      const sortedIdx = [...idxList].sort((a, b) => a - b);
      sortedIdx.forEach((i) => {
        const url = (d.images || [])[i];
        if (url) collected.push(url);
      });
      newImagesByDraft[draftId] = (d.images || []).filter((_, i) => !idxList.includes(i));
    });

    if (collected.length === 0) {
      toast.message('Keine verschiebbaren Bilder ausgewählt');
      return;
    }

    const newTargetImages = [...(target.images || []), ...collected];
    newImagesByDraft[targetDraftId] = newTargetImages;

    setBulkBusy(true);
    // Optimistic UI
    setDrafts((prev) => prev.map((d) => {
      if (d.id in newImagesByDraft) return { ...d, images: newImagesByDraft[d.id] };
      return d;
    }));

    try {
      await Promise.all(
        Object.entries(newImagesByDraft).map(([draftId, images]) =>
          axiosInstance.put(`/products/drafts/${draftId}`, { images })
        )
      );
      toast.success(`${collected.length} ${collected.length === 1 ? 'Bild' : 'Bilder'} zu „${target.name || 'Draft'}" verschoben`);
      clearImageSelection();
      setBulkMoveTarget('');
    } catch (e) {
      toast.error('Bulk-Verschieben fehlgeschlagen — Stand wird neu geladen');
      fetchDrafts();
    } finally {
      setBulkBusy(false);
    }
  };

  // Bulk DELETE all selected images from their source drafts
  const bulkDeleteImages = async () => {
    if (selectedImages.size === 0) return;
    if (!window.confirm(`${selectedImages.size} Bild(er) endgültig aus allen Drafts entfernen?`)) return;
    const byDraft = groupSelectionBySource(selectedImages);
    const newImagesByDraft = {};
    Object.entries(byDraft).forEach(([draftId, idxList]) => {
      const d = drafts.find((x) => x.id === draftId);
      if (!d) return;
      newImagesByDraft[draftId] = (d.images || []).filter((_, i) => !idxList.includes(i));
    });
    setBulkBusy(true);
    setDrafts((prev) => prev.map((d) => d.id in newImagesByDraft ? { ...d, images: newImagesByDraft[d.id] } : d));
    try {
      await Promise.all(
        Object.entries(newImagesByDraft).map(([draftId, images]) =>
          axiosInstance.put(`/products/drafts/${draftId}`, { images })
        )
      );
      toast.success(`${selectedImages.size} Bild(er) entfernt`);
      clearImageSelection();
    } catch (e) {
      toast.error('Löschen fehlgeschlagen — Stand wird neu geladen');
      fetchDrafts();
    } finally {
      setBulkBusy(false);
    }
  };

  // Append additional images to an existing draft (separate file picker per card)
  const appendImagesToDraft = async (draftId, files) => {
    const arr = Array.from(files || []);
    if (arr.length === 0) return;
    setAppendBusyId(draftId);
    try {
      const fd = new FormData();
      for (const f of arr) fd.append('files', f);
      const res = await axiosInstance.post(`/products/drafts/${draftId}/upload-images`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90_000,
      });
      const { added, images } = res.data || {};
      setDrafts((prev) => prev.map((d) => d.id === draftId ? { ...d, images } : d));
      toast.success(`${added} ${added === 1 ? 'Bild' : 'Bilder'} hinzugefügt`);
    } catch (e) {
      toast.error('Hinzufügen fehlgeschlagen: ' + (e.response?.data?.detail || e.message));
    } finally {
      setAppendBusyId(null);
    }
  };

  // Reorder or remove images on a draft → persist the new images[] order
  const updateDraftImages = async (draftId, newImages) => {
    // Optimistic UI update
    setDrafts((prev) => prev.map((d) => d.id === draftId ? { ...d, images: newImages } : d));
    try {
      await axiosInstance.put(`/products/drafts/${draftId}`, { images: newImages });
    } catch (e) {
      toast.error('Bilder konnten nicht gespeichert werden');
    }
  };

  // Move an image from one draft to another (drag-and-drop across cards).
  // `toIndex = null` → append to end of target's images.
  const moveImageBetweenDrafts = async (fromDraftId, fromIndex, toDraftId, toIndex = null) => {
    if (fromDraftId === toDraftId) return;
    const source = drafts.find((d) => d.id === fromDraftId);
    const target = drafts.find((d) => d.id === toDraftId);
    if (!source || !target) return;
    const imageUrl = (source.images || [])[fromIndex];
    if (!imageUrl) return;

    const newSourceImages = (source.images || []).filter((_, i) => i !== fromIndex);
    const newTargetImages = [...(target.images || [])];
    if (toIndex === null || toIndex < 0 || toIndex > newTargetImages.length) {
      newTargetImages.push(imageUrl);
    } else {
      newTargetImages.splice(toIndex, 0, imageUrl);
    }

    // Optimistic UI update both cards at once
    setDrafts((prev) => prev.map((d) => {
      if (d.id === fromDraftId) return { ...d, images: newSourceImages };
      if (d.id === toDraftId) return { ...d, images: newTargetImages };
      return d;
    }));

    try {
      await Promise.all([
        axiosInstance.put(`/products/drafts/${fromDraftId}`, { images: newSourceImages }),
        axiosInstance.put(`/products/drafts/${toDraftId}`, { images: newTargetImages }),
      ]);
      toast.success(`Bild zu „${target.name || 'Draft'}" verschoben`);
    } catch (e) {
      toast.error('Verschieben fehlgeschlagen — bitte erneut versuchen');
      // Rollback on error
      fetchDrafts();
    }
  };

  // ---- AI: single-draft analysis ----
  const analyzeDraft = async (draft) => {
    setAiBusyId(draft.id);
    try {
      const res = await axiosInstance.post(`/products/drafts/ai/${draft.id}/analyze`);
      const suggestion = res.data?.suggestion;
      if (suggestion) {
        setAiSuggestionFor({ draft, suggestion });
        // Refresh local state so the suggestion is also stored on the card
        setDrafts((prev) => prev.map((d) => d.id === draft.id ? { ...d, ai_suggestion: suggestion } : d));
      }
    } catch (e) {
      toast.error('KI-Analyse fehlgeschlagen: ' + (e.response?.data?.detail || e.message));
    } finally {
      setAiBusyId(null);
    }
  };

  const applySuggestion = async (draft, suggestion, fields, values) => {
    try {
      await axiosInstance.post(`/products/drafts/ai/${draft.id}/apply-suggestion`, {
        fields,
        values: values || null,
      });
      toast.success('Vorschlag übernommen ✨');
      setAiSuggestionFor(null);
      await fetchDrafts();
    } catch (e) {
      toast.error('Übernehmen fehlgeschlagen');
    }
  };

  // ---- AI: cluster (duplicate / variant detection) ----
  // silent=true → no toasts, no dialog opened; caller uses returned groups for banner
  const runCluster = async ({ silent = false } = {}) => {
    setClusterBusy(true);
    try {
      const res = await axiosInstance.post('/products/drafts/ai/cluster', {});
      const groups = res.data?.groups || [];
      if (silent) {
        // Quietly return — caller (handleFiles) sets pendingClusterGroups for the banner.
        // Important: do NOT touch clusterGroups here, that would open the dialog (cond: !== null).
      } else if (groups.length === 0) {
        toast.info(res.data?.note || 'Keine Zusammenfass-Vorschläge gefunden.');
        // Skip opening an empty dialog — toast is enough for the manual flow.
      } else {
        toast.success(`${groups.length} ${groups.length === 1 ? 'Vorschlag' : 'Vorschläge'} gefunden`);
        setClusterGroups(groups);
      }
      return groups;
    } catch (e) {
      if (!silent) {
        toast.error('Duplikat-Erkennung fehlgeschlagen: ' + (e.response?.data?.detail || e.message));
      }
      return [];
    } finally {
      setClusterBusy(false);
    }
  };

  const applyCluster = async (group) => {
    if (!group?.members || group.members.length < 2) return;
    const keep = group.members[0].id;
    const merge_ids = group.members.slice(1).map(m => m.id);
    try {
      await axiosInstance.post('/products/drafts/ai/cluster/apply', {
        keep_id: keep,
        merge_ids,
        new_name: group.label || undefined,
      });
      toast.success(`${group.members.length} Drafts zu einem Artikel zusammengeführt ✨`);
      // Remove this group from the dialog AND the pending banner
      setClusterGroups((prev) => (prev || []).filter(g => g !== group));
      setPendingClusterGroups((prev) => (prev || []).filter(g => g !== group));
      await fetchDrafts();
    } catch (e) {
      toast.error('Zusammenführen fehlgeschlagen: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Load drafts + categories on mount
  const fetchDrafts = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/products/drafts');
      setDrafts(res.data || []);
    } catch (e) {
      toast.error('Drafts konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDrafts();
    axiosInstance.get('/categories').then(r => setCategories(r.data || [])).catch(() => {});
    axiosInstance.get('/categories/subcategory-config').then(r => setSubcategoryConfig(r.data?.config || {})).catch(() => {});
  }, [fetchDrafts]);

  // ------- Upload (batched to survive proxy size/time limits on big uploads) -------
  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const BATCH_SIZE = 10;
    let totalGroups = 0;
    let totalUpdated = 0;
    let uploadedCount = 0;
    const errors = [];
    try {
      for (let i = 0; i < files.length; i += BATCH_SIZE) {
        const batch = files.slice(i, i + BATCH_SIZE);
        const fd = new FormData();
        for (const f of batch) fd.append('files', f);
        try {
          const res = await axiosInstance.post('/products/drafts/upload-images', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 90_000,
          });
          const { groups, updated } = res.data || {};
          totalGroups += (groups || 0);
          totalUpdated += (updated?.length || 0);
          uploadedCount += batch.length;
          toast.info(
            `Upload ${uploadedCount}/${files.length} Bilder…`,
            { id: 'upload-progress' }
          );
        } catch (be) {
          errors.push(`Batch ${i / BATCH_SIZE + 1}: ${be.response?.data?.detail || be.message}`);
        }
      }
      if (uploadedCount > 0) {
        toast.success(
          `${uploadedCount} ${uploadedCount === 1 ? 'Bild' : 'Bilder'} hochgeladen → ${totalGroups} ${totalGroups === 1 ? 'Gruppe' : 'Gruppen'} erkannt`,
          { id: 'upload-progress' }
        );
      }
      if (totalUpdated > 0) {
        toast.info(`${totalUpdated} bestehende Draft(s) erweitert`);
      }
      if (errors.length) {
        toast.error(`${errors.length} Batch(es) fehlgeschlagen — bitte erneut versuchen:\n${errors.slice(0, 2).join('\n')}`, { duration: 9000 });
      }
      await fetchDrafts();

      // Auto-cluster after upload (silent mode) — only if we actually got new groups
      if (totalGroups > 0 || totalUpdated > 0) {
        setAutoClusterRunning(true);
        try {
          const groups = await runCluster({ silent: true });
          setPendingClusterGroups(groups || []);
        } finally {
          setAutoClusterRunning(false);
        }
      }
    } finally {
      setUploading(false);
    }
  };

  const onFileInputChange = (e) => {
    handleFiles(Array.from(e.target.files || []));
    e.target.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleFiles(Array.from(e.dataTransfer.files || []));
  };
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };

  // Neuer leerer Draft — Container-Karte ohne Bilder, in die der Admin nachträglich Fotos zieht.
  const createEmptyDraft = async () => {
    try {
      const res = await axiosInstance.post('/products/drafts/create-empty');
      const newId = res.data?.draft?.id;
      toast.success('Neuer leerer Artikel angelegt — jetzt Bilder hinzufügen');
      await fetchDrafts();
      // Zur neuen Karte scrollen
      if (newId) {
        setTimeout(() => {
          const el = document.querySelector(`[data-draft-id="${newId}"]`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 200);
      }
      return newId;
    } catch (e) {
      toast.error(`Konnte keinen leeren Artikel anlegen: ${e.response?.data?.detail || e.message}`);
      return null;
    }
  };

  // Markierte Bilder in einen frisch angelegten leeren Draft verschieben.
  const bulkMoveToNewDraft = async () => {
    if (selectedImages.size === 0) return;
    setBulkBusy(true);
    try {
      const res = await axiosInstance.post('/products/drafts/create-empty');
      const newDraft = res.data?.draft;
      if (!newDraft) throw new Error('Neuer Draft konnte nicht erstellt werden');

      // URLs der markierten Bilder aus den bestehenden Drafts einsammeln + aus Quell-Drafts entfernen
      const byDraft = groupSelectionBySource(selectedImages);
      const collected = [];
      const updatesBySrc = {};
      Object.entries(byDraft).forEach(([srcId, idxList]) => {
        const d = drafts.find((x) => x.id === srcId);
        if (!d) return;
        const idxSet = new Set(idxList);
        [...idxList].sort((a, b) => a - b).forEach((i) => {
          const url = (d.images || [])[i];
          if (url) collected.push(url);
        });
        updatesBySrc[srcId] = (d.images || []).filter((_, i) => !idxSet.has(i));
      });

      if (collected.length === 0) {
        toast.message('Keine verschiebbaren Bilder ausgewählt');
        // Draft trotzdem behalten? Ja — Admin wollte einen neuen. Nur Toast.
        await fetchDrafts();
        return;
      }

      // Bilder auf neuen Draft schreiben + aus Quelle entfernen — parallel
      await Promise.all([
        axiosInstance.put(`/products/drafts/${newDraft.id}`, { images: collected }),
        ...Object.entries(updatesBySrc).map(([srcId, images]) =>
          axiosInstance.put(`/products/drafts/${srcId}`, { images })
        ),
      ]);

      toast.success(
        `${collected.length} ${collected.length === 1 ? 'Bild' : 'Bilder'} in neuen Artikel verschoben`
      );
      clearImageSelection();
      setBulkMoveTarget('');
      await fetchDrafts();
      setTimeout(() => {
        const el = document.querySelector(`[data-draft-id="${newDraft.id}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    } catch (e) {
      toast.error(`Verschieben in neuen Artikel fehlgeschlagen: ${e.response?.data?.detail || e.message}`);
      fetchDrafts();
    } finally {
      setBulkBusy(false);
    }
  };

  // ------- Edit + Save -------
  const updateField = (id, field, value) => {
    setDrafts((prev) =>
      prev.map((d) => (d.id === id ? { ...d, [field]: value, __dirty: true } : d))
    );
  };

  const saveDraft = async (draft) => {
    try {
      const salePrice = draft.sale_price && parseFloat(draft.sale_price) > 0
        ? parseFloat(draft.sale_price) : null;
      const origPrice = parseFloat(draft.price) || 0;
      const payload = {
        name: draft.name,
        price: origPrice,
        sale_price: salePrice,
        stock: parseInt(draft.stock) || 0,
        category: draft.category || '',
        subcategory: draft.subcategory || '',
        brand: draft.brand || '',
        description: draft.description || '',
      };
      await axiosInstance.put(`/products/drafts/${draft.id}`, payload);
      setDrafts((prev) => prev.map((d) => (d.id === draft.id ? { ...d, __dirty: false } : d)));
      toast.success('Gespeichert');
      // SALE-Bestätigung sichtbar machen wenn aktiv
      if (salePrice && origPrice > 0 && salePrice < origPrice) {
        const pct = Math.round((1 - salePrice / origPrice) * 100);
        toast.success(
          `🔥 −${pct}% Rabatt aktiv · live nach Veröffentlichung im SALE-Banner, Shop & /sale`,
          { duration: 5000 }
        );
      }
    } catch (e) {
      toast.error('Speichern fehlgeschlagen');
    }
  };

  const deleteDraft = async (draft) => {
    if (!window.confirm(`„${draft.name}" und ${draft.images?.length || 0} Bilder wirklich verwerfen?`)) return;
    try {
      await axiosInstance.delete(`/products/drafts/${draft.id}`);
      setDrafts((prev) => prev.filter((d) => d.id !== draft.id));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(draft.id);
        return next;
      });
      toast.success('Draft verworfen');
    } catch (e) {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  // ------- Publish -------
  /**
   * Run duplicate-detector for the given draft IDs.
   * Returns a dict {draftId: [matches]} for drafts that have at least one duplicate.
   * Empty dict means no duplicates, safe to proceed.
   */
  const _scanDuplicates = async (ids) => {
    const found = {};
    for (const id of ids) {
      const d = drafts.find(x => x.id === id);
      if (!d || !d.name) continue;
      try {
        const r = await axiosInstance.post('/products/check-duplicate', {
          name: d.name,
          images: d.images || [],
          category: d.category || null,
        });
        if (r.data?.is_duplicate && r.data.similar_products?.length) {
          // Skip self-match (same id) just in case
          const matches = r.data.similar_products.filter(p => p.id !== d.id);
          if (matches.length) found[id] = matches;
        }
      } catch (e) {
        // Don't block on scan errors — log and continue
        console.warn('duplicate scan failed', id, e);
      }
    }
    return found;
  };

  const publishOne = async (id, force = false) => {
    if (!force) {
      const dupes = await _scanDuplicates([id]);
      if (Object.keys(dupes).length) {
        setDuplicateInfo({ draftIds: [id], duplicatesByDraft: dupes, mode: 'single' });
        return;
      }
    }
    try {
      const res = await axiosInstance.post(`/products/drafts/${id}/publish`);
      if (res.data?.published?.length) {
        toast.success('Veröffentlicht ✨');
        await fetchDrafts();
      } else if (res.data?.skipped?.length) {
        const problems = res.data.skipped[0]?.problems?.join(', ');
        toast.error(`Veröffentlichen nicht möglich: ${problems}`);
      }
    } catch (e) {
      toast.error('Veröffentlichen fehlgeschlagen');
    }
  };

  const bulkPublish = async (force = false) => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    if (!force) {
      const dupes = await _scanDuplicates(ids);
      if (Object.keys(dupes).length) {
        setDuplicateInfo({ draftIds: ids, duplicatesByDraft: dupes, mode: 'bulk' });
        return;
      }
    }
    try {
      const res = await axiosInstance.post('/products/drafts/bulk-publish', {
        ids,
      });
      const { published_count, skipped_count, skipped } = res.data || {};
      if (published_count > 0) {
        toast.success(`${published_count} Artikel veröffentlicht ✨`);
      }
      if (skipped_count > 0) {
        toast.warning(
          `${skipped_count} übersprungen — bitte fehlende Felder ergänzen`,
          { description: skipped.map(s => `${s.name}: ${s.problems.join(', ')}`).join(' | ') }
        );
      }
      setSelectedIds(new Set());
      await fetchDrafts();
    } catch (e) {
      toast.error('Bulk-Veröffentlichen fehlgeschlagen');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === drafts.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(drafts.map(d => d.id)));
  };

  // ------- Render -------
  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="drafts-manager">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin')}
            className="text-[#5D4E3C] hover:bg-[#FAF5EE]"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Zurück
          </Button>
          <div>
            <h1 className="text-xl md:text-2xl font-semibold text-[#5D4E3C]" style={{ fontFamily: 'Georgia, serif' }}>
              Artikel vorbereiten
            </h1>
            <p className="text-xs text-[#8B7355] mt-0.5">
              Bilder per Drag-and-Drop hochladen — wir gruppieren automatisch nach Dateinamen.
              Drafts sind nicht öffentlich, bis du sie freigibst.
            </p>
          </div>
        </div>
      </div>

      {/* Collapsible help box — "Was machen die Buttons?" */}
      <div className="mb-4 border border-[#E8DCC8] bg-[#FAF5EE]" data-testid="drafts-help-box">
        <button
          type="button"
          onClick={() => setHelpOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-[#F5EBDD] transition-colors"
          data-testid="drafts-help-toggle"
        >
          <span className="flex items-center gap-2 text-[#5D4E3C] font-medium text-sm">
            <HelpCircle className="h-4 w-4 text-[#C4A084]" />
            Hilfe — Was bedeuten die Buttons?
          </span>
          {helpOpen
            ? <ChevronUp className="h-4 w-4 text-[#8B7355]" />
            : <ChevronDown className="h-4 w-4 text-[#8B7355]" />}
        </button>

        {helpOpen && (
          <div className="px-4 pb-4 pt-1 border-t border-[#E8DCC8] space-y-3 text-[13px] text-[#5D4E3C] leading-relaxed">
            <div className="flex items-start gap-3">
              <Upload className="h-4 w-4 text-[#C4A084] mt-0.5 shrink-0" />
              <div>
                <strong>Bilder hierhin ziehen / klicken</strong> — Lädt mehrere Bilder
                gleichzeitig hoch. Wir gruppieren automatisch nach Dateinamen, sodass
                z.B. <code className="px-1 bg-white border border-[#E8DCC8] rounded">Haargummi-Rot-1.jpg</code> +
                <code className="px-1 bg-white border border-[#E8DCC8] rounded">Haargummi-Rot-2.jpg</code>
                <strong> einen</strong> Artikel mit 2 Bildern ergeben.
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Plus className="h-4 w-4 text-[#C4A084] mt-0.5 shrink-0" />
              <div>
                <strong>&bdquo;+ Bilder&ldquo; auf der Karte</strong> — Fügt einem bereits existierenden
                Draft <strong>weitere</strong> Bilder hinzu. Praktisch, wenn du später
                noch Detailaufnahmen nachreichst.
              </div>
            </div>
            <div className="flex items-start gap-3">
              <GripVertical className="h-4 w-4 text-[#C4A084] mt-0.5 shrink-0" />
              <div>
                <strong>Bilder umsortieren (Drag &amp; Drop)</strong> — Greife ein Bild
                und ziehe es an die gewünschte Position. Das <strong>erste Bild</strong> ist
                automatisch das Cover-Bild im Shop.
              </div>
            </div>
            <div className="flex items-start gap-3">
              <GripVertical className="h-4 w-4 text-[#C4A084] mt-0.5 shrink-0" />
              <div>
                <strong>Bilder zwischen Artikeln verschieben</strong> — Du kannst ein Bild
                auch direkt in <strong>einen anderen Artikel ziehen</strong> (z.B. von einem
                Draft weiter unten in einen darüber). Das Bild verschwindet aus dem
                Quell-Artikel und erscheint im Ziel-Artikel. Praktisch, wenn die
                automatische Gruppierung mal danebenliegt.
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-4 w-4 text-[#C4A084] mt-0.5 shrink-0" />
              <div>
                <strong>Mehrere Bilder gleichzeitig verschieben</strong> — Klicke auf
                den ✓-Kreis unten links eines Bildes, um es zu markieren. Du kannst
                Bilder aus <strong>verschiedenen Drafts</strong> markieren und sie dann
                über die untere Toolbar in einen Ziel-Draft verschieben oder
                gemeinsam löschen.
              </div>
            </div>
            <div className="flex items-start gap-3">
              <X className="h-4 w-4 text-[#C4A084] mt-0.5 shrink-0" />
              <div>
                <strong>Bild entfernen</strong> — Das X auf jedem Bild löscht nur dieses
                eine Bild aus dem Draft (Original bleibt im Speicher).
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Wand2 className="h-4 w-4 text-[#C4A084] mt-0.5 shrink-0" />
              <div>
                <strong>KI-Analyse</strong> — Die KI schaut sich das Cover-Bild an und
                schlägt Name, Kategorie und Beschreibung vor. Du kannst alles vor der
                Übernahme bearbeiten.
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Layers className="h-4 w-4 text-[#C4A084] mt-0.5 shrink-0" />
              <div>
                <strong>Duplikate erkennen</strong> — Die KI sucht in deinen Drafts nach
                Bildern, die zum selben Artikel gehören (z.B. Farbvarianten), und schlägt
                dir vor, sie zusammenzuführen.
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-4 w-4 text-[#3F7A3F] mt-0.5 shrink-0" />
              <div>
                <strong>Speichern</strong> — Sichert deine Eingaben (Name, Preis, …) im
                Draft. Erscheint erst, wenn du etwas geändert hast.
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Sparkles className="h-4 w-4 text-[#C4A084] mt-0.5 shrink-0" />
              <div>
                <strong>Freigeben</strong> — Macht aus dem Draft einen <strong>öffentlich
                sichtbaren</strong> Artikel im Shop. Funktioniert nur, wenn Name, Preis und
                Kategorie ausgefüllt sind.
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Trash2 className="h-4 w-4 text-[#B0512E] mt-0.5 shrink-0" />
              <div>
                <strong>Verwerfen (Mülleimer)</strong> — Löscht den ganzen Draft inkl.
                aller Bilder. Veröffentlichte Artikel sind davon nicht betroffen.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Auto-Cluster: pending KI suggestions banner */}
      {(autoClusterRunning || (pendingClusterGroups && pendingClusterGroups.length > 0)) && !clusterBusy && (
        <div
          className="mb-4 p-3 bg-[#FFF8E7] border-l-4 border-[#C4A084] flex items-center justify-between gap-3 flex-wrap"
          data-testid="auto-cluster-banner"
        >
          {autoClusterRunning ? (
            <div className="flex items-center gap-2 text-sm text-[#5D4E3C]">
              <Wand2 className="h-4 w-4 text-[#C4A084] animate-pulse" />
              <span>Die KI prüft gerade, ob noch Bilder zusammengehören…</span>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 text-sm text-[#5D4E3C]">
                <Wand2 className="h-4 w-4 text-[#C4A084]" />
                <span>
                  <strong>{pendingClusterGroups.length}</strong>{' '}
                  {pendingClusterGroups.length === 1 ? 'Gruppe' : 'Gruppen'} könnten zusammengeführt werden.
                </span>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => {
                    setClusterGroups(pendingClusterGroups);
                    setPendingClusterGroups([]);
                  }}
                  className="bg-[#C4A084] hover:bg-[#B89968] text-white"
                  data-testid="auto-cluster-review-btn"
                >
                  Vorschläge ansehen
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setPendingClusterGroups([])}
                  className="text-[#8B7355] hover:text-[#5D4E3C]"
                  data-testid="auto-cluster-dismiss-btn"
                >
                  Später
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Style-Guide-Hint */}
      <div
        className="mb-4 p-3 border-l-4 border-[#C4A084] bg-[#FAF5EE] text-[13px] text-[#5D4E3C] leading-relaxed flex items-start gap-2"
      >
        <span className="text-base leading-none mt-0.5">💡</span>
        <div>
          <strong>Tipp für die Gruppierung:</strong> Benenne Bilder gleicher
          Artikel mit dem gleichen Basis-Namen und einer Nummer, z.B.
          <code className="px-1 mx-1 bg-white border border-[#E8DCC8] rounded">Haargummi-Rot-1.jpg</code>,
          <code className="px-1 mx-1 bg-white border border-[#E8DCC8] rounded">Haargummi-Rot-2.jpg</code>,
          <code className="px-1 mx-1 bg-white border border-[#E8DCC8] rounded">Haargummi-Rot-3.jpg</code>
          – wir erkennen das als <strong>einen</strong> Artikel mit 3 Bildern.
        </div>
      </div>

      {/* Drop zone + Neuer leerer Artikel */}
      <div className="grid gap-3 md:grid-cols-[1fr,auto] mb-6">
        <div
          onDrop={onDrop}
          onDragOver={onDragOver}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed p-6 md:p-8 text-center cursor-pointer transition-colors ${
            uploading ? 'border-[#C4A084] bg-[#FAF5EE] cursor-wait' : 'border-[#C4A084]/60 hover:border-[#C4A084] hover:bg-[#FAF5EE]'
          }`}
          data-testid="drafts-drop-zone"
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            className="hidden"
            onChange={onFileInputChange}
            data-testid="drafts-file-input"
          />
          <Upload className="h-10 w-10 mx-auto mb-2 text-[#C4A084]" />
          <p className="text-base text-[#5D4E3C] font-medium mb-1">
            {uploading ? 'Bilder werden hochgeladen…' : 'Bilder hierhin ziehen oder klicken'}
          </p>
          <p className="text-xs text-[#8B7355]">
            JPG / PNG / WEBP / GIF · max. 8 MB pro Bild · bis zu 100 Bilder pro Upload
          </p>
        </div>

        <button
          type="button"
          onClick={createEmptyDraft}
          disabled={uploading}
          className="flex flex-col items-center justify-center gap-1.5 border-2 border-dashed border-[#8B7355]/60 hover:border-[#8B7355] hover:bg-[#FAF5EE] p-6 md:p-8 md:min-w-[220px] text-[#5D4E3C] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="drafts-create-empty"
          title="Legt einen leeren Artikel-Container an – Bilder kannst du danach per Drag & Drop oder + Bilder-Button in die Karte ziehen."
        >
          <Plus className="h-8 w-8 text-[#8B7355]" />
          <span className="text-sm font-semibold">+ Neuer leerer Artikel</span>
          <span className="text-[11px] text-[#8B7355] text-center leading-snug">
            Container ohne Bilder anlegen<br />und später Fotos hineinziehen
          </span>
        </button>
      </div>

      {/* Stats + bulk actions */}
      {drafts.length > 0 && (
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap" data-testid="drafts-toolbar">
          <div className="flex items-center gap-2 text-sm text-[#5D4E3C]">
            <span><strong>{drafts.length}</strong> {drafts.length === 1 ? 'Draft' : 'Drafts'}</span>
            <span className="text-[#C4A084]">·</span>
            <span><strong>{selectedIds.size}</strong> ausgewählt</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={runCluster}
              disabled={clusterBusy || drafts.length < 2}
              className="border-[#8B7355] text-[#5D4E3C] hover:bg-[#FAF5EE]"
              data-testid="drafts-ai-cluster"
              title="KI sucht ähnliche Drafts (z.B. Farbvarianten) und schlägt vor, sie zusammenzuführen"
            >
              <Layers className="h-4 w-4 mr-1.5" />
              {clusterBusy ? 'KI analysiert…' : 'Duplikate erkennen'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={selectAll}
              className="border-[#C4A084] text-[#5D4E3C] hover:bg-[#FAF5EE]"
              data-testid="drafts-select-all"
            >
              {selectedIds.size === drafts.length ? 'Auswahl aufheben' : 'Alle auswählen'}
            </Button>
            <Button
              onClick={bulkPublish}
              disabled={selectedIds.size === 0}
              className="bg-[#C4A084] hover:bg-[#B89968] text-white"
              data-testid="drafts-bulk-publish"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              {selectedIds.size > 0
                ? `${selectedIds.size} veröffentlichen`
                : 'Veröffentlichen'}
            </Button>
          </div>
        </div>
      )}

      {/* Drafts list */}
      {loading ? (
        <p className="text-center text-[#8B7355] py-12">Lade Drafts…</p>
      ) : drafts.length === 0 ? (
        <div className="text-center py-12 bg-[#FAF5EE] border border-[#E8DCC8]">
          <ImageIcon className="h-10 w-10 mx-auto mb-2 text-[#C4A084]" />
          <p className="text-sm text-[#6D5A47]">Noch keine Drafts. Lade oben Bilder hoch, um zu starten.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {drafts.map((d) => (
            <DraftCard
              key={d.id}
              draft={d}
              selected={selectedIds.has(d.id)}
              onSelect={() => toggleSelect(d.id)}
              onUpdate={updateField}
              onSave={() => saveDraft(d)}
              onDelete={() => deleteDraft(d)}
              onPublish={() => publishOne(d.id)}
              onAnalyze={() => analyzeDraft(d)}
              onAppendImages={(files) => appendImagesToDraft(d.id, files)}
              onImagesChange={(imgs) => updateDraftImages(d.id, imgs)}
              onMoveImageFrom={moveImageBetweenDrafts}
              selectedImages={selectedImages}
              onToggleImageSelect={toggleImageSelection}
              appendBusy={appendBusyId === d.id}
              aiBusy={aiBusyId === d.id}
              categories={categories}
              subcategoryConfig={subcategoryConfig}
            />
          ))}
        </div>
      )}

      {/* Floating Bulk-Action Toolbar (minimizable + jump-to-draft chips) */}
      {selectedImages.size > 0 && (
        <>
          {/* Bottom spacer so page content isn't hidden under the toolbar */}
          <div aria-hidden="true" style={{ height: toolbarMinimized ? 56 : 180 }} />

          {toolbarMinimized ? (
            /* MINIMIZED PILL */
            <button
              type="button"
              onClick={() => setToolbarMinimized(false)}
              className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-[#5D4E3C] text-white shadow-xl px-4 py-2.5 flex items-center gap-2 text-sm"
              data-testid="bulk-toolbar-expand"
            >
              <ChevronUp className="h-4 w-4" />
              <span className="font-semibold">{selectedImages.size} markiert</span>
              <span className="opacity-70 text-xs">· Aktionen zeigen</span>
            </button>
          ) : (
            /* FULL TOOLBAR */
            <div
              className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t-2 border-[#C4A084] shadow-2xl"
              data-testid="bulk-image-toolbar"
            >
              {/* Header: title + minimize */}
              <div className="flex items-center justify-between px-3 py-2 border-b border-[#F0E6D6]">
                <span className="text-sm font-semibold text-[#5D4E3C]">
                  {selectedImages.size} {selectedImages.size === 1 ? 'Bild markiert' : 'Bilder markiert'}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setToolbarMinimized(true)}
                    className="p-1.5 hover:bg-[#FAF5EE] text-[#8B7355]"
                    title="Aktionsleiste minimieren, damit du frei scrollen kannst"
                    data-testid="bulk-toolbar-minimize"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={clearImageSelection}
                    disabled={bulkBusy}
                    className="p-1.5 hover:bg-[#FDECEA] text-[#B0512E]"
                    title="Markierung aufheben"
                    data-testid="bulk-clear-btn"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Quick-Jump Chips: alle Drafts als scroll-into-view-Buttons */}
              {drafts.length > 0 && (
                <div className="px-3 py-2 border-b border-[#F0E6D6] bg-[#FBF6EE]">
                  <div className="text-[10px] uppercase tracking-widest text-[#8B7355] mb-1.5">
                    Zum Artikel springen (tippen)
                  </div>
                  <div className="flex gap-1.5 overflow-x-auto pb-1" style={{ WebkitOverflowScrolling: 'touch' }}>
                    {drafts.map((d) => {
                      const isTarget = bulkMoveTarget === d.id;
                      return (
                        <button
                          key={d.id}
                          type="button"
                          onClick={() => {
                            setBulkMoveTarget(d.id);
                            const el = document.querySelector(`[data-draft-id="${d.id}"]`);
                            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }}
                          className="shrink-0 text-xs px-2.5 py-1.5 border whitespace-nowrap transition-colors"
                          style={{
                            borderColor: isTarget ? '#5D4E3C' : '#E5D9C8',
                            backgroundColor: isTarget ? '#5D4E3C' : 'white',
                            color: isTarget ? 'white' : '#5D4E3C',
                          }}
                          data-testid={`draft-jump-${d.id}`}
                        >
                          {isTarget && '✓ '}{d.name || 'Unbenannt'} · {(d.images || []).length}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Aktions-Reihe */}
              <div className="flex flex-wrap items-center gap-2 px-3 py-2.5">
                <button
                  type="button"
                  onClick={() => bulkMoveImages(bulkMoveTarget)}
                  disabled={!bulkMoveTarget || bulkBusy}
                  className="text-sm bg-[#5D4E3C] hover:bg-[#4A3E30] text-white px-3 py-2 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                  data-testid="bulk-move-confirm-btn"
                >
                  {bulkBusy ? 'Verschiebe…' : (<><CheckCircle2 className="h-4 w-4" /> Hierher verschieben</>)}
                </button>
                <button
                  type="button"
                  onClick={bulkMoveToNewDraft}
                  disabled={bulkBusy}
                  className="text-sm border border-[#8B7355] text-[#5D4E3C] hover:bg-[#FAF5EE] px-3 py-2 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                  data-testid="bulk-move-new-draft-btn"
                  title="Markierte Bilder in einen frisch angelegten leeren Artikel verschieben"
                >
                  <Plus className="h-4 w-4" /> Neuer Artikel
                </button>
                <button
                  type="button"
                  onClick={bulkDeleteImages}
                  disabled={bulkBusy}
                  className="ml-auto text-sm text-[#B0512E] hover:bg-[#FDECEA] px-3 py-2 flex items-center gap-1 disabled:opacity-40"
                  data-testid="bulk-delete-btn"
                  title="Markierte Bilder aus allen Drafts entfernen"
                >
                  <Trash2 className="h-4 w-4" /> Löschen
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* AI Suggestion Dialog (single draft) */}
      {aiSuggestionFor && (
        <SuggestionDialog
          open={!!aiSuggestionFor}
          onClose={() => setAiSuggestionFor(null)}
          draft={aiSuggestionFor.draft}
          suggestion={aiSuggestionFor.suggestion}
          onApply={(fields, values) => applySuggestion(aiSuggestionFor.draft, aiSuggestionFor.suggestion, fields, values)}
        />
      )}

      {/* Cluster Dialog */}
      {clusterGroups !== null && (
        <ClusterDialog
          open={clusterGroups !== null}
          onClose={() => setClusterGroups(null)}
          groups={clusterGroups}
          allDrafts={drafts}
          onApply={applyCluster}
        />
      )}

      {/* Duplicate Warning Dialog */}
      {duplicateInfo && (
        <Dialog open={!!duplicateInfo} onOpenChange={(o) => !o && setDuplicateInfo(null)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-[#8B5A3C]">
                ⚠️ Mögliche Duplikate erkannt
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <p className="text-sm text-[#6D5A47]">
                {duplicateInfo.mode === 'bulk'
                  ? `${Object.keys(duplicateInfo.duplicatesByDraft).length} von ${duplicateInfo.draftIds.length} ausgewählten Entwürfen haben Ähnlichkeiten zu bereits bestehenden Artikeln.`
                  : 'Dieser Entwurf ähnelt einem oder mehreren bereits gespeicherten Artikeln.'}
              </p>
              {Object.entries(duplicateInfo.duplicatesByDraft).map(([dId, matches]) => {
                const draft = drafts.find(d => d.id === dId);
                return (
                  <div key={dId} className="border border-[#E8DCC8] rounded-lg p-3 bg-[#FAF5EF]">
                    <div className="flex items-center gap-2 mb-2 text-sm">
                      <span className="font-semibold text-[#5D4E3C]">Entwurf:</span>
                      <span>{draft?.name || '—'}</span>
                    </div>
                    <div className="space-y-2">
                      {matches.map((p) => (
                        <div key={p.id} className="flex gap-3 items-start bg-white border border-[#E8DCC8] p-2">
                          {p.images?.[0] && (
                            <img src={p.images[0]} alt={p.name} className="w-16 h-16 object-cover" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{p.name}</p>
                            <p className="text-xs text-[#8B7355]">{p.category} · {p.price?.toFixed?.(2) ?? p.price}€</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {p.match_reasons.map((r, i) => (
                                <span key={i} className="text-[10px] bg-[#FFF4E6] text-[#8B5A3C] px-1.5 py-0.5 rounded">
                                  {r}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setDuplicateInfo(null)}
                data-testid="duplicate-cancel-btn"
              >
                Abbrechen
              </Button>
              <Button
                onClick={async () => {
                  const info = duplicateInfo;
                  setDuplicateInfo(null);
                  if (info.mode === 'single') {
                    await publishOne(info.draftIds[0], true);
                  } else {
                    await bulkPublish(true);
                  }
                }}
                className="bg-[#5D4E3C] hover:bg-[#3E2723] text-white"
                data-testid="duplicate-force-publish-btn"
              >
                Trotzdem veröffentlichen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

/* ---------- DraftCard ---------- */
const DRAFT_IMAGE_MIME = 'application/x-draft-image';

const DraftCard = ({
  draft, selected, onSelect, onUpdate, onSave, onDelete, onPublish, onAnalyze,
  onAppendImages, onImagesChange, onMoveImageFrom, selectedImages, onToggleImageSelect,
  appendBusy, aiBusy, categories, subcategoryConfig = {},
}) => {
  const dirty = !!draft.__dirty;
  const ready = draft.name && draft.name !== 'Neuer Artikel' && draft.category && parseFloat(draft.price) > 0;
  const appendInputRef = useRef(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [externalDragOver, setExternalDragOver] = useState(false);

  const images = draft.images || [];

  const handleDragStart = (e, idx) => {
    setDragIndex(idx);
    e.dataTransfer.effectAllowed = 'move';
    // Carry source draftId so OTHER cards can detect cross-card drops
    const payload = JSON.stringify({ draftId: draft.id, index: idx });
    try { e.dataTransfer.setData(DRAFT_IMAGE_MIME, payload); } catch (_) { /* noop */ }
    try { e.dataTransfer.setData('text/plain', payload); } catch (_) { /* noop */ }
  };

  // Read a drop payload (returns {draftId, index} or null)
  const readDropPayload = (e) => {
    let raw = '';
    try { raw = e.dataTransfer.getData(DRAFT_IMAGE_MIME); } catch (_) { /* noop */ }
    if (!raw) {
      try { raw = e.dataTransfer.getData('text/plain'); } catch (_) { /* noop */ }
    }
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && parsed.draftId && typeof parsed.index === 'number') {
        return parsed;
      }
    } catch (_) { /* noop */ }
    return null;
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== idx) setDragOverIndex(idx);
  };
  const handleDragLeave = () => setDragOverIndex(null);

  const handleDrop = (e, idx) => {
    e.preventDefault();
    e.stopPropagation();
    const payload = readDropPayload(e);
    setDragIndex(null);
    setDragOverIndex(null);
    setExternalDragOver(false);

    // Cross-card drop?
    if (payload && payload.draftId && payload.draftId !== draft.id) {
      onMoveImageFrom?.(payload.draftId, payload.index, draft.id, idx);
      return;
    }

    // Intra-card reorder
    const fromIdx = payload?.index ?? dragIndex;
    if (fromIdx === null || fromIdx === undefined || fromIdx === idx) return;
    const next = images.slice();
    const [moved] = next.splice(fromIdx, 1);
    next.splice(idx, 0, moved);
    onImagesChange?.(next);
  };

  // Drop on the empty area / "+ Bilder" tile = append at end (cross-card)
  const handleStripDragOver = (e) => {
    // If user is dragging an image FROM this same card, don't highlight as external
    if (dragIndex !== null) return;
    if (e.dataTransfer.types?.includes(DRAFT_IMAGE_MIME) || e.dataTransfer.types?.includes('text/plain')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setExternalDragOver(true);
    }
  };
  const handleStripDragLeave = (e) => {
    // Only clear if leaving the strip (not entering a child)
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setExternalDragOver(false);
  };
  const handleStripDrop = (e) => {
    const payload = readDropPayload(e);
    setExternalDragOver(false);
    if (!payload) return;
    if (payload.draftId && payload.draftId !== draft.id) {
      e.preventDefault();
      e.stopPropagation();
      onMoveImageFrom?.(payload.draftId, payload.index, draft.id, null);
    }
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
    setExternalDragOver(false);
  };

  const removeImage = (idx) => {
    const next = images.filter((_, i) => i !== idx);
    onImagesChange?.(next);
  };

  return (
    <div
      className={`border bg-white p-3 md:p-4 ${selected ? 'border-[#C4A084] shadow-sm' : 'border-[#E8DCC8]'}`}
      data-testid={`draft-card-${draft.id}`}
      data-draft-id={draft.id}
    >
      <div className="flex items-start gap-3 mb-3">
        <input
          type="checkbox"
          checked={selected}
          onChange={onSelect}
          className="mt-1 h-4 w-4 accent-[#C4A084]"
          data-testid={`draft-checkbox-${draft.id}`}
        />
        <div className="flex-1 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 text-xs text-[#8B7355]">
            <span className="px-2 py-0.5 bg-[#FFF8E1] text-[#8B6F00] rounded">📝 Draft</span>
            <span>SKU: {draft.sku}</span>
            {draft.draft_group_key && <span>· Gruppe: {draft.draft_group_key}</span>}
            <span className={`flex items-center gap-1 ${ready ? 'text-[#3F7A3F]' : 'text-[#B0512E]'}`}>
              {ready ? <CheckCircle2 className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
              {ready ? 'Bereit zum Veröffentlichen' : 'Fehlende Felder'}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="outline"
              onClick={onAnalyze}
              disabled={aiBusy}
              className="border-[#8B7355] text-[#5D4E3C] hover:bg-[#FAF5EE]"
              data-testid={`draft-ai-${draft.id}`}
              title="KI analysiert das Foto und schlägt Name, Kategorie und Beschreibung vor"
            >
              <Wand2 className="h-3.5 w-3.5 mr-1" />
              {aiBusy ? 'KI…' : 'KI-Analyse'}
            </Button>
            {dirty && (
              <Button
                size="sm"
                variant="outline"
                onClick={onSave}
                className="border-[#C4A084] text-[#5D4E3C] hover:bg-[#FAF5EE]"
                data-testid={`draft-save-${draft.id}`}
              >
                Speichern
              </Button>
            )}
            <Button
              size="sm"
              onClick={onPublish}
              disabled={!ready}
              className="bg-[#C4A084] hover:bg-[#B89968] text-white"
              data-testid={`draft-publish-${draft.id}`}
            >
              Freigeben
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onDelete}
              className="text-[#B0512E] hover:bg-[#FBE4DA]"
              data-testid={`draft-delete-${draft.id}`}
              title="Verwerfen"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Image strip — draggable + delete + add + cross-card move */}
      <div
        className={`flex gap-2 overflow-x-auto pb-2 mb-3 scrollbar-hide items-start transition-colors ${
          externalDragOver ? 'bg-[#FAF5EE] ring-2 ring-[#C4A084]/60 ring-inset' : ''
        }`}
        data-testid={`draft-images-${draft.id}`}
        onDragOver={handleStripDragOver}
        onDragLeave={handleStripDragLeave}
        onDrop={handleStripDrop}
      >
        {images.map((src, i) => {
          const isCover = i === 0;
          const isDragOver = dragOverIndex === i;
          const isBeingDragged = dragIndex === i;
          const selectKey = `${draft.id}:${i}`;
          const isSelected = selectedImages?.has(selectKey);
          return (
            <div
              key={`${draft.id}-img-${i}`}
              draggable
              onDragStart={(e) => handleDragStart(e, i)}
              onDragOver={(e) => handleDragOver(e, i)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, i)}
              onDragEnd={handleDragEnd}
              className={`relative flex-shrink-0 w-24 h-24 md:w-20 md:h-20 border overflow-hidden bg-[#FAF5EE] cursor-move transition-all ${
                isSelected ? 'border-[#5D4E3C] ring-2 ring-[#5D4E3C]' : (isCover ? 'border-[#C4A084] ring-1 ring-[#C4A084]' : 'border-[#E8DCC8]')
              } ${isDragOver && !isBeingDragged ? 'ring-2 ring-[#5D4E3C]' : ''} ${isBeingDragged ? 'opacity-40' : ''}`}
              data-testid={`draft-image-${draft.id}-${i}`}
              title={isCover ? 'Cover-Bild (erstes Bild im Shop)' : `Bild ${i + 1} — Drag zum Umsortieren`}
            >
              <img
                src={src}
                alt={`${draft.name}-${i + 1}`}
                className="w-full h-full object-cover pointer-events-none"
                loading="lazy"
                draggable={false}
              />
              {/* Selection checkbox (subtle, no big white background) */}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onToggleImageSelect?.(draft.id, i); }}
                className={`absolute bottom-0.5 left-0.5 h-4 w-4 rounded-sm border flex items-center justify-center transition-colors ${
                  isSelected ? 'bg-[#5D4E3C] border-[#5D4E3C] text-white' : 'bg-white/70 backdrop-blur-[2px] border-[#C4A084]/70 text-transparent hover:text-[#C4A084] hover:bg-white/95'
                }`}
                title={isSelected ? 'Markierung aufheben' : 'Markieren (für Bulk-Verschieben)'}
                data-testid={`draft-image-select-${draft.id}-${i}`}
              >
                <CheckCircle2 className="h-3 w-3" />
              </button>
              {isCover && (
                <span
                  className="absolute top-0.5 left-0.5 bg-[#C4A084]/90 text-white text-[7px] tracking-wider uppercase px-1 py-px leading-none flex items-center gap-0.5 rounded-sm"
                  title="Cover-Bild"
                >
                  <Star className="h-1.5 w-1.5" />
                </span>
              )}
              <button
                type="button"
                onClick={() => removeImage(i)}
                className="absolute top-0.5 right-0.5 bg-black/40 hover:bg-red-500 text-white rounded-full h-4 w-4 flex items-center justify-center shadow-sm transition-colors"
                title="Bild entfernen"
                data-testid={`draft-image-remove-${draft.id}-${i}`}
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </div>
          );
        })}

        {/* + Bilder hinzufügen */}
        <button
          type="button"
          onClick={() => appendInputRef.current?.click()}
          disabled={appendBusy}
          className="flex-shrink-0 w-24 h-24 md:w-20 md:h-20 border-2 border-dashed border-[#C4A084]/60 hover:border-[#C4A084] hover:bg-[#FAF5EE] flex flex-col items-center justify-center text-[#8B7355] hover:text-[#5D4E3C] transition-colors disabled:opacity-50 disabled:cursor-wait"
          data-testid={`draft-add-images-${draft.id}`}
          title="Weitere Bilder zu diesem Artikel hinzufügen"
        >
          {appendBusy
            ? <span className="text-[10px] animate-pulse">Lädt…</span>
            : (
              <>
                <Plus className="h-5 w-5" />
                <span className="text-[9px] tracking-wider uppercase mt-0.5">Bilder</span>
              </>
            )}
        </button>
        <input
          ref={appendInputRef}
          type="file"
          multiple
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            if (e.target.files && e.target.files.length) onAppendImages?.(e.target.files);
            e.target.value = '';
          }}
          data-testid={`draft-add-images-input-${draft.id}`}
        />
      </div>

      {/* Schnelleingabe */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
        <div className="md:col-span-4">
          <Label className="text-[11px] tracking-wider text-[#8B7355] uppercase">Name</Label>
          <Input
            value={draft.name || ''}
            onChange={(e) => onUpdate(draft.id, 'name', e.target.value)}
            className="text-sm border-[#E8DCC8] focus:border-[#C4A084]"
            data-testid={`draft-name-${draft.id}`}
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-[11px] tracking-wider text-[#8B7355] uppercase">Preis €</Label>
          <Input
            type="number"
            step="0.01"
            value={draft.price ?? ''}
            onChange={(e) => onUpdate(draft.id, 'price', e.target.value)}
            className="text-sm border-[#E8DCC8] focus:border-[#C4A084]"
            data-testid={`draft-price-${draft.id}`}
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-[11px] tracking-wider text-[#B0512E] uppercase flex items-center gap-1">
            <span className="px-1 py-px bg-[#B0512E] text-white text-[9px] tracking-wider">SALE</span>
            <span>€ (optional)</span>
          </Label>
          <div className="relative">
            <Input
              type="number"
              step="0.01"
              placeholder="–"
              value={draft.sale_price ?? ''}
              onChange={(e) => onUpdate(draft.id, 'sale_price', e.target.value)}
              className="text-sm border-[#E8DCC8] focus:border-[#B0512E]"
              data-testid={`draft-sale-price-${draft.id}`}
            />
            {draft.sale_price && parseFloat(draft.sale_price) > 0 && parseFloat(draft.price) > 0 && parseFloat(draft.sale_price) < parseFloat(draft.price) && (
              <span className="absolute -top-1 -right-1 text-[9px] font-bold text-white bg-[#B0512E] px-1 py-px">
                −{Math.round((1 - parseFloat(draft.sale_price) / parseFloat(draft.price)) * 100)}%
              </span>
            )}
          </div>
        </div>
        <div className="md:col-span-2">
          <Label className="text-[11px] tracking-wider text-[#8B7355] uppercase">Bestand</Label>
          <Input
            type="number"
            value={draft.stock ?? ''}
            onChange={(e) => onUpdate(draft.id, 'stock', e.target.value)}
            className="text-sm border-[#E8DCC8] focus:border-[#C4A084]"
            data-testid={`draft-stock-${draft.id}`}
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-[11px] tracking-wider text-[#8B7355] uppercase">Kategorie</Label>
          <select
            value={draft.category || ''}
            onChange={(e) => onUpdate(draft.id, 'category', e.target.value)}
            className="w-full h-9 px-2 text-sm border border-[#E8DCC8] focus:border-[#C4A084] focus:outline-none bg-white"
            data-testid={`draft-category-${draft.id}`}
          >
            <option value="">— wählen —</option>
            {categories.map((c) => (
              <option key={c.name || c} value={c.name || c}>{c.name || c}</option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-[11px] tracking-wider text-[#8B7355] uppercase">Subkategorie</Label>
          {subcategoryConfig[draft.category] && subcategoryConfig[draft.category].length > 0 ? (
            <select
              value={draft.subcategory || ''}
              onChange={(e) => onUpdate(draft.id, 'subcategory', e.target.value)}
              className="w-full text-sm border border-[#E8DCC8] bg-white px-3 py-2 focus:border-[#C4A084] outline-none rounded-md h-10"
              data-testid={`draft-subcategory-${draft.id}`}
            >
              <option value="">— wählen —</option>
              {subcategoryConfig[draft.category].map((sub) => (
                <option key={sub} value={sub}>{sub}</option>
              ))}
            </select>
          ) : (
            <Input
              value={draft.subcategory || ''}
              onChange={(e) => onUpdate(draft.id, 'subcategory', e.target.value)}
              className="text-sm border-[#E8DCC8] focus:border-[#C4A084]"
              data-testid={`draft-subcategory-${draft.id}`}
              placeholder="Optional"
            />
          )}
        </div>
        <div className="md:col-span-12">
          <Label className="text-[11px] tracking-wider text-[#8B7355] uppercase">Beschreibung (optional)</Label>
          <Input
            value={draft.description || ''}
            onChange={(e) => onUpdate(draft.id, 'description', e.target.value)}
            placeholder="Kurze Beschreibung des Artikels…"
            className="text-sm border-[#E8DCC8] focus:border-[#C4A084]"
            data-testid={`draft-description-${draft.id}`}
          />
        </div>
      </div>
    </div>
  );
};

export default ProductDraftsManager;

/* ---------- Suggestion Dialog (single draft) ---------- */
const ALL_FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'category', label: 'Kategorie' },
  { key: 'subcategory', label: 'Subkategorie' },
  { key: 'color', label: 'Farbe' },
  { key: 'material', label: 'Material' },
  { key: 'description', label: 'Beschreibung' },
];

const SuggestionDialog = ({ open, onClose, draft, suggestion, onApply }) => {
  const [selected, setSelected] = useState(() => new Set(['name', 'category', 'subcategory', 'description']));
  const [values, setValues] = useState({});

  // Bei jedem Öffnen die editierbaren Werte mit dem KI-Vorschlag vorbelegen
  useEffect(() => {
    if (open && suggestion) {
      const initial = {};
      ALL_FIELDS.forEach(({ key }) => {
        initial[key] = suggestion[key] ?? '';
      });
      setValues(initial);
      setSelected(new Set(['name', 'category', 'subcategory', 'description']));
    }
  }, [open, suggestion]);

  const toggle = (k) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  const updateValue = (key, val) => setValues(prev => ({ ...prev, [key]: val }));

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ai-suggestion-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#5D4E3C]">
            <Wand2 className="h-5 w-5 text-[#C4A084]" />
            KI-Vorschlag für &bdquo;{draft?.name || 'Draft'}&ldquo;
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          {(suggestion.confidence != null) && (
            <div className="text-xs text-[#8B7355]">
              Sicherheit der KI: <strong>{Math.round((suggestion.confidence || 0) * 100)}%</strong> · Du kannst jeden Vorschlag vor dem Übernehmen noch anpassen.
            </div>
          )}
          <div className="space-y-2">
            {ALL_FIELDS.map(({ key, label }) => {
              const originalVal = suggestion[key];
              if (!originalVal) return null;
              const isMultiline = key === 'description';
              return (
                <div
                  key={key}
                  className="flex items-start gap-2 p-2 border border-[#E8DCC8]"
                  data-testid={`ai-field-${key}`}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(key)}
                    onChange={() => toggle(key)}
                    className="mt-2 accent-[#C4A084]"
                    aria-label={`${label} übernehmen`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] uppercase tracking-wider text-[#8B7355] mb-1">{label}</p>
                    {isMultiline ? (
                      <textarea
                        value={values[key] ?? ''}
                        onChange={(e) => updateValue(key, e.target.value)}
                        rows={4}
                        className="w-full border border-[#E8DCC8] focus:border-[#C4A084] outline-none px-2 py-1.5 text-sm text-[#3D3D3D] resize-y"
                        data-testid={`ai-edit-${key}`}
                      />
                    ) : (
                      <input
                        type="text"
                        value={values[key] ?? ''}
                        onChange={(e) => updateValue(key, e.target.value)}
                        className="w-full border border-[#E8DCC8] focus:border-[#C4A084] outline-none px-2 py-1.5 text-sm text-[#3D3D3D]"
                        data-testid={`ai-edit-${key}`}
                      />
                    )}
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      {originalVal && values[key] !== originalVal && (
                        <button
                          type="button"
                          onClick={() => updateValue(key, originalVal)}
                          className="text-[10px] text-[#8B7355] hover:text-[#5D4E3C] underline"
                          data-testid={`ai-reset-${key}`}
                        >
                          KI-Vorschlag zurücksetzen
                        </button>
                      )}
                      {draft?.[key] && draft[key] !== values[key] && (
                        <span className="text-[10px] text-[#A89A85]">
                          Aktuell im Draft: <span className="italic">{String(draft[key]).slice(0, 60)}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {suggestion.tags?.length > 0 && (
              <div className="p-2 border border-[#E8DCC8]">
                <p className="text-[11px] uppercase tracking-wider text-[#8B7355] mb-1">Tags / Stimmung</p>
                <div className="flex flex-wrap gap-1.5">
                  {suggestion.tags.map((t, i) => (
                    <span key={i} className="px-2 py-0.5 text-xs bg-[#FAF5EE] border border-[#E8DCC8] text-[#5D4E3C]">{t}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="ai-suggestion-cancel">Abbrechen</Button>
          <Button
            onClick={() => onApply(Array.from(selected), values)}
            disabled={selected.size === 0}
            className="bg-[#C4A084] hover:bg-[#B89968] text-white"
            data-testid="ai-suggestion-apply"
          >
            Ausgewählte übernehmen ({selected.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ---------- Cluster Dialog ---------- */
const ConfidenceBadge = ({ confidence }) => {
  const c = Number(confidence) || 0;
  let level, label, classes;
  if (c >= 0.85) {
    level = 'high';
    label = 'Sehr sicher';
    classes = 'bg-green-100 text-green-800 border-green-300';
  } else if (c >= 0.6) {
    level = 'medium';
    label = 'Wahrscheinlich';
    classes = 'bg-amber-100 text-amber-800 border-amber-300';
  } else {
    level = 'low';
    label = 'Unsicher';
    classes = 'bg-red-100 text-red-800 border-red-300';
  }
  const dot = level === 'high' ? '🟢' : level === 'medium' ? '🟡' : '🔴';
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] tracking-wider uppercase px-1.5 py-0.5 border font-semibold ${classes}`}
      title={`KI-Sicherheit: ${Math.round(c * 100)}%`}
      data-testid={`cluster-confidence-${level}`}
    >
      <span>{dot}</span> {label} · {Math.round(c * 100)}%
    </span>
  );
};

const ClusterDialog = ({ open, onClose, groups, allDrafts, onApply }) => {
  const lookup = new Map((allDrafts || []).map(d => [d.id, d]));

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="ai-cluster-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#5D4E3C]">
            <Layers className="h-5 w-5 text-[#C4A084]" />
            Vorschläge zum Zusammenführen
          </DialogTitle>
        </DialogHeader>

        <div className="text-xs text-[#8B7355] -mt-2 mb-2 flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-1">🟢 <strong>Sehr sicher</strong> (≥85%) — kannst du blind übernehmen</span>
          <span className="inline-flex items-center gap-1">🟡 <strong>Wahrscheinlich</strong> (60-84%) — kurz prüfen</span>
          <span className="inline-flex items-center gap-1">🔴 <strong>Unsicher</strong> (&lt;60%) — sorgfältig prüfen</span>
        </div>

        {groups.length === 0 ? (
          <p className="text-sm text-[#6D5A47] py-6 text-center">
            Keine Vorschläge gefunden — alle Drafts sehen unterschiedlich aus.
          </p>
        ) : (
          <div className="space-y-4">
            {[...groups].sort((a, b) => (b.confidence || 0) - (a.confidence || 0)).map((g, gi) => (
              <div key={gi} className="border border-[#E8DCC8] p-3" data-testid={`cluster-group-${gi}`}>
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-[#5D4E3C] text-sm">{g.label}</p>
                      <ConfidenceBadge confidence={g.confidence} />
                    </div>
                    {g.variant_kind && (
                      <p className="text-[11px] text-[#C4A084] uppercase tracking-wider">
                        Variantenart: {g.variant_kind}
                      </p>
                    )}
                    {g.reason && (
                      <p className="text-xs text-[#8B7355] mt-1 italic">{g.reason}</p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => onApply(g)}
                    className="bg-[#C4A084] hover:bg-[#B89968] text-white"
                    data-testid={`cluster-apply-${gi}`}
                  >
                    Zusammenführen ({g.members.length})
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {g.members.map((m) => {
                    const d = lookup.get(m.id);
                    const thumb = d?.images?.[0];
                    return (
                      <div key={m.id} className="flex items-center gap-2 bg-[#FAF5EE] border border-[#E8DCC8] p-1.5 pr-2">
                        {thumb ? (
                          <img src={thumb} alt={m.name} className="h-12 w-12 object-cover" loading="lazy" />
                        ) : (
                          <div className="h-12 w-12 bg-[#E8DCC8]" />
                        )}
                        <span className="text-xs text-[#5D4E3C]">{m.name || d?.name || m.id.slice(0, 6)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="ai-cluster-close">Schließen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
